/**
 * CriteriaLinkingContent Component
 *
 * Main content component for the Criteria tab in PlaybookLinkageTabs.
 * Displays linked criteria for the selected playbook with unlink actions,
 * provides a picker modal to add criteria, and supports inline editing
 * of criteria items via the CriteriaModal.
 */

import React, { useState, useCallback, useEffect } from 'react';

import { LinkedCriteriaList } from './LinkedCriteriaList';
import { CriteriaLinkPickerModal } from './CriteriaLinkPickerModal';
import { CriteriaModal } from '../Criteria/CriteriaModal';
import { useStrategiesPlaybooks } from '../context/StrategiesPlaybooksContext';
import { criteriaService, marketConditionCategoryService } from '@/services';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';

export type CriteriaLinkingContentProps = Record<string, never>;

export const CriteriaLinkingContent: React.FC<CriteriaLinkingContentProps> = () => {
  const { localPlaybookCriteriaIds, setLocalPlaybookCriteriaIds } = useStrategiesPlaybooks();

  // Picker modal state
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Edit modal state
  const [editingCriteria, setEditingCriteria] = useState<PlaybookCriteriaItem | undefined>(
    undefined
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Shared saving state for both edit and create modals
  const [isSaving, setIsSaving] = useState(false);

  // Categories for edit modal
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [allCategories, setAllCategories] = useState<MarketConditionCategory[]>([]);

  /**
   * Load categories for the edit modal
   * Called on mount and before opening edit modal to ensure fresh data
   */
  const loadCategories = useCallback(async () => {
    try {
      const allCats = await marketConditionCategoryService.getAll();
      setAllCategories(allCats);
      setCategories(allCats.filter((c) => c.status === 'active'));
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  /**
   * Handle unlinking a single criteria item
   * Removes the ID from localPlaybookCriteriaIds, marking the playbook dirty
   * Uses functional update to handle rapid sequential unlinks correctly
   */
  const handleUnlink = useCallback(
    (criteriaId: string) => {
      setLocalPlaybookCriteriaIds((prev) => prev.filter((id) => id !== criteriaId));
    },
    [setLocalPlaybookCriteriaIds]
  );

  /**
   * Handle selection change from the picker modal
   * Replaces localPlaybookCriteriaIds with the new selection
   */
  const handleSelectionChange = useCallback(
    (selectedIds: string[]) => {
      setLocalPlaybookCriteriaIds(selectedIds);
    },
    [setLocalPlaybookCriteriaIds]
  );

  /**
   * Handle edit action from the linked list
   * Refreshes categories and opens the edit modal with the selected criteria
   */
  const handleEdit = useCallback(
    async (criteria: PlaybookCriteriaItem) => {
      // Refresh categories before opening modal to ensure fresh data
      await loadCategories();
      setEditingCriteria(criteria);
      setIsEditModalOpen(true);
    },
    [loadCategories]
  );

  /**
   * Handle closing the edit modal
   * Clears editingCriteria state
   */
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingCriteria(undefined);
  }, []);

  /**
   * Handle save from the edit modal
   * Updates the criteria via service and notifies sync
   * No manual data reload needed - LinkedCriteriaList's sync subscription handles refresh
   */
  const handleSave = useCallback(
    async (data: { name: string; description?: string; marketConditionCategoryId?: string }) => {
      if (!editingCriteria) return;

      setIsSaving(true);
      try {
        await criteriaService.update(editingCriteria.id, data);
        syncCoordinatorService.notifyLocalChange('playbook_criteria_items', editingCriteria.id);
        syncCoordinatorService.notifyChange('playbook_criteria_items', editingCriteria.id);
        handleCloseEditModal();
      } catch (error) {
        console.error('Failed to save criteria:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [editingCriteria, handleCloseEditModal]
  );

  /**
   * Handle opening the create modal
   * Clears any stale editing state, loads fresh categories, then opens modal
   */
  const handleOpenCreateModal = useCallback(async () => {
    // Clear any stale editing state to prevent data flash
    setEditingCriteria(undefined);
    // Load fresh categories before opening modal
    await loadCategories();
    setIsCreateModalOpen(true);
  }, [loadCategories]);

  /**
   * Handle closing the create modal
   */
  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  /**
   * Handle create from the create modal
   * Creates new criteria via service, notifies sync, auto-links to playbook, closes modal
   */
  const handleCreate = useCallback(
    async (data: { name: string; description?: string; marketConditionCategoryId?: string }) => {
      setIsSaving(true);
      try {
        // Build criteria data, only including optional fields if they have values
        // (required for exactOptionalPropertyTypes)
        const criteriaData: { name: string; description?: string; marketConditionCategoryId?: string } = {
          name: data.name,
        };
        if (data.description) {
          criteriaData.description = data.description;
        }
        if (data.marketConditionCategoryId) {
          criteriaData.marketConditionCategoryId = data.marketConditionCategoryId;
        }

        // Create the new criteria entity
        const newCriteria = await criteriaService.add(criteriaData);

        // Notify sync for same-tab and cross-tab updates
        syncCoordinatorService.notifyLocalChange('playbook_criteria_items', newCriteria.id);
        syncCoordinatorService.notifyChange('playbook_criteria_items', newCriteria.id);

        // Auto-link the new criteria to the current playbook
        setLocalPlaybookCriteriaIds((prev) => [...prev, newCriteria.id]);

        handleCloseCreateModal();
      } catch (error) {
        console.error('Failed to create criteria:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [setLocalPlaybookCriteriaIds, handleCloseCreateModal]
  );

  // Determine which modal is open and configure props accordingly
  const isModalOpen = isEditModalOpen || isCreateModalOpen;
  const modalCriteria = isCreateModalOpen ? undefined : editingCriteria;
  const handleModalClose = isCreateModalOpen ? handleCloseCreateModal : handleCloseEditModal;
  const handleModalSave = isCreateModalOpen ? handleCreate : handleSave;

  return (
    <>
      {/* Linked Criteria List with unlink, add existing, create new, and edit actions */}
      <LinkedCriteriaList
        criteriaIds={localPlaybookCriteriaIds}
        onUnlink={handleUnlink}
        onAdd={() => setIsPickerOpen(true)}
        onCreateNew={handleOpenCreateModal}
        onEdit={handleEdit}
      />

      {/* Criteria Picker Modal */}
      <CriteriaLinkPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        selectedCriteriaIds={localPlaybookCriteriaIds}
        onSelectionChange={handleSelectionChange}
      />

      {/* Criteria Modal - reused for both edit and create modes */}
      <CriteriaModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        criteria={modalCriteria}
        categories={categories}
        allCategories={allCategories}
        onSave={handleModalSave}
        isSaving={isSaving}
      />
    </>
  );
};
